/**
 * AgendaItemFormDialog — unified form for Agendamento / Visita de
 * orçamento / Lembrete. Step 1 picks the type (chips), step 2 shows
 * the shared form. Only budgets in "aprovado / iniciando / em_andamento"
 * appear in the budget selector — matches the user's requirement.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SearchableCombobox from "@/components/SearchableCombobox";
import { Calendar, Clock, Megaphone, ClipboardList, AlertCircle, Trash2 } from "lucide-react";

const TYPES = [
  { value: "agendamento",      label: "Agendamento",         Icon: Calendar },
  { value: "visita_orcamento", label: "Visita de orçamento", Icon: ClipboardList },
  { value: "lembrete",         label: "Lembrete",            Icon: Megaphone },
];
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AgendaItemFormDialog({ open, onOpenChange, editing, defaultDate, onSaved }) {
  const isEdit = !!editing;
  const [type, setType] = useState("agendamento");
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [budgetId, setBudgetId] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [observation, setObservation] = useState("");
  const [materials, setMaterials] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  const [budgets, setBudgets] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setType(editing.type || "agendamento");
      setDate(editing.date || todayIso());
      setTime(editing.time || "");
      setTitle(editing.title || "");
      setSchoolId(editing.school_id || "");
      setBudgetId(editing.budget_id || "");
      setSelectedItemIds(new Set(editing.item_ids || []));
      setObservation(editing.observation || "");
      setMaterials(editing.materials || "");
      setPriority(editing.priority || "normal");
    } else {
      setType("agendamento");
      setDate((defaultDate || todayIso()).slice(0, 10));
      setTime("");
      setTitle("");
      setSchoolId("");
      setBudgetId("");
      setSelectedItemIds(new Set());
      setObservation("");
      setMaterials("");
      setPriority("normal");
    }
    setLoadingBudgets(true);
    api.get("/agenda/available-budgets")
      .then((r) => setBudgets(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBudgets([]))
      .finally(() => setLoadingBudgets(false));
  }, [open, editing]);

  const selectedBudget = useMemo(
    () => budgets.find((b) => b.id === budgetId) || null,
    [budgets, budgetId],
  );

  // Build the School selector options from the available-budgets payload:
  // a school is eligible only if it has at least one budget whose status
  // is already in ELIGIBLE_BUDGET_STATUSES on the backend.
  const schoolOptions = useMemo(() => {
    const seen = new Map();
    for (const b of budgets) {
      if (b.school_id && !seen.has(b.school_id)) {
        seen.set(b.school_id, { value: b.school_id, label: b.school_name || "(sem nome)" });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [budgets]);

  // Budget options derived from the picked school.  Each option exposes
  // the number + status + service summary so the user can tell them
  // apart without opening the budget.
  const STATUS_LABEL = {
    aprovado: "Aprovado",
    iniciando: "Em andamento",
    finalizado: "Finalizado",
  };
  const budgetOptions = useMemo(() => {
    if (!schoolId) return [];
    return budgets
      .filter((b) => b.school_id === schoolId)
      .map((b) => ({
        value: b.id,
        label: `Nº ${b.number}${b.service_description ? ` — ${b.service_description}` : ""}`,
        sub: STATUS_LABEL[b.status] || b.status,
      }));
  }, [budgets, schoolId]);

  const toggleItem = (id) =>
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submit = async () => {
    if (!date) return toast.error("Informe a data");
    setSaving(true);
    try {
      const payload = {
        type,
        date,
        time: time || null,
        title: title.trim() || null,
        school_id: schoolId || null,
        budget_id: budgetId || null,
        item_ids: selectedItemIds.size ? Array.from(selectedItemIds) : null,
        observation: observation.trim() || null,
        materials: materials.trim() || null,
        priority,
      };
      if (isEdit) {
        await api.patch(`/agenda/${editing.id}`, payload);
        toast.success("Atualizado");
      } else {
        await api.post("/agenda", payload);
        toast.success("Adicionado ao cronograma");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Excluir este item do cronograma?")) return;
    setSaving(true);
    try {
      await api.delete(`/agenda/${editing.id}`);
      toast.success("Excluído do cronograma");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    } finally {
      setSaving(false);
    }
  };

  const showBudgetBlock = type === "agendamento" || type === "visita_orcamento";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0"
        data-testid="agenda-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-fuchsia-600" />
            {isEdit ? "Editar item" : "Novo item do cronograma"}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Preencha só o que fizer sentido. Na visualização, campos vazios ficam escondidos.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — type */}
        {(
          <div>
            <label className="block text-[11px] font-black uppercase text-slate-600 mb-1.5">Tipo</label>
            <div className="grid grid-cols-3 gap-1.5">
              {TYPES.map((t) => {
                const Ic = t.Icon;
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    data-testid={`agenda-type-${t.value}`}
                    className={`rounded-lg border-2 py-2 px-1.5 flex flex-col items-center gap-1 transition-colors ${
                      active
                        ? "border-fuchsia-600 bg-fuchsia-50 text-fuchsia-900"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <Ic className="w-4 h-4" />
                    <span className="text-[10.5px] font-bold leading-tight text-center">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Date + time + priority */}
        <div className="grid grid-cols-1 gap-2">
          <div className="min-w-0">
            <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Data</label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="agenda-date"
              className="h-10 w-full min-w-0 block appearance-none text-left"
            />
          </div>
          <div className="min-w-0">
            <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Hora</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="agenda-time"
              className="h-10 w-full min-w-0 block appearance-none text-left"
            />
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Prioridade</label>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { v: "baixa",  l: "Baixa",  c: "border-slate-200 text-slate-600" },
              { v: "normal", l: "Normal", c: "border-sky-300 text-sky-700" },
              { v: "alta",   l: "Alta",   c: "border-rose-400 text-rose-700" },
            ].map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => setPriority(p.v)}
                data-testid={`agenda-priority-${p.v}`}
                className={`rounded-md border-2 py-1 text-[11px] font-bold transition-colors ${
                  priority === p.v
                    ? `${p.c} bg-slate-50`
                    : "border-slate-200 text-slate-500 bg-white hover:border-slate-300"
                }`}
              >
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {/* Budget block — only when relevant.  First the user picks a
            school (searchable), then the budget list is filtered to
            budgets of that school in status aprovado / iniciando /
            finalizado — matching the "Nova despesa" flow. */}
        {showBudgetBlock && (
          <div className="space-y-2 min-w-0">
            <div className="min-w-0">
              <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Escola</label>
              <SearchableCombobox
                value={schoolId}
                onChange={(v) => { setSchoolId(v); setBudgetId(""); setSelectedItemIds(new Set()); }}
                options={schoolOptions}
                placeholder={loadingBudgets ? "Carregando..." : (schoolOptions.length ? "Selecione uma escola" : "Nenhuma escola elegível")}
                searchPlaceholder="Buscar escola..."
                emptyLabel="Nenhuma escola encontrada"
                wrap
                data-testid="agenda-school-select"
              />
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                Apenas escolas com orçamentos <b>Aprovado</b>, <b>Em andamento</b> ou <b>Finalizado</b>.
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Orçamento</label>
              <div className="rounded-md bg-sky-50 border border-sky-200 px-2 py-1.5 flex items-start gap-1.5 mb-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-sky-700 shrink-0 mt-0.5" />
                <p className="text-[11px] text-sky-900 leading-snug">
                  Lista filtrada por escola e pelos status <strong>aprovado / iniciando / finalizado</strong>.
                </p>
              </div>
              <SearchableCombobox
                value={budgetId}
                onChange={(v) => {
                  setBudgetId(v);
                  setSelectedItemIds(new Set());
                }}
                options={budgetOptions}
                placeholder={
                  !schoolId
                    ? "Selecione uma escola primeiro"
                    : (budgetOptions.length ? "Nenhum (opcional)" : "Sem orçamentos elegíveis nesta escola")
                }
                searchPlaceholder="Buscar nº, serviço..."
                emptyLabel="Nada encontrado"
                disabled={!schoolId}
                wrap
                data-testid="agenda-budget-select"
              />
            </div>

            {selectedBudget && selectedBudget.items.length > 0 && (
              <div className="mt-2 rounded-md border border-slate-200 bg-white p-2 max-h-32 overflow-y-auto">
                <div className="text-[10px] font-bold text-slate-500 mb-1">
                  Itens do orçamento (opcional)
                </div>
                <ul className="space-y-0.5">
                  {selectedBudget.items.map((it) => (
                    <li key={it.id}>
                      <label className="flex items-start gap-1.5 text-[11px] cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.has(it.id)}
                          onChange={() => toggleItem(it.id)}
                          data-testid={`agenda-item-check-${it.id}`}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 break-words">{it.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Title (optional override) */}
        <div>
          <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Título (opcional)</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Deixe em branco pra gerar automático"
            data-testid="agenda-title"
            className="h-9 text-xs"
          />
        </div>

        {/* Observation */}
        <div>
          <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Observação</label>
          <Textarea
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            rows={2}
            placeholder="Detalhes, combinados, instruções..."
            data-testid="agenda-observation"
            className="text-xs resize-none"
          />
        </div>

        {/* Materials */}
        <div>
          <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">
            Material necessário <span className="text-slate-400 font-normal">(uma linha por item)</span>
          </label>
          <Textarea
            value={materials}
            onChange={(e) => setMaterials(e.target.value)}
            rows={3}
            placeholder={"Ex.:\n2 sacos de cimento\n1 lata tinta branca"}
            data-testid="agenda-materials"
            className="text-xs resize-none font-mono"
          />
          <p className="mt-1 text-[10px] text-slate-500">
            Vão para a <strong>lista de compras consolidada</strong> do dia.
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          {isEdit && (
            <Button
              variant="outline"
              onClick={remove}
              disabled={saving}
              data-testid="agenda-form-delete"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Excluir
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="agenda-save"
            className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          >
            {saving ? "Salvando..." : isEdit ? "Salvar" : "Adicionar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
